import { Controller } from "@hotwired/stimulus"

type StripeConnectGlobal = {
  init: (options: {
    publishableKey: string
    fetchClientSecret: () => Promise<string | undefined>
  }) => {
    create: (componentName: string) => HTMLElement
  }
  onLoad?: () => void
}

declare global {
  interface Window {
    StripeConnect?: StripeConnectGlobal
    nativeBridge?: {
      postMessage: (message: string) => void
      supportedComponents?: string[]
    }
  }
}

export default class extends Controller {
  static values = {
    publishableKey: String,
    accountSessionUrl: String,
    accountLinkUrl: String,
    statusCheckUrl: String,
    businessType: { type: String, default: "individual" },
    useNative: { type: Boolean, default: false }
  }

  static targets = ["container", "error", "startButton", "syncButton", "buttonText", "spinner", "businessType", "header"]

  declare publishableKeyValue: string
  declare accountSessionUrlValue: string
  declare accountLinkUrlValue: string
  declare statusCheckUrlValue: string
  declare businessTypeValue: string
  declare useNativeValue: boolean

  declare containerTarget: HTMLElement
  declare errorTarget: HTMLElement
  declare startButtonTarget: HTMLButtonElement
  declare syncButtonTarget: HTMLButtonElement
  declare buttonTextTarget: HTMLElement
  declare spinnerTarget: HTMLElement
  declare businessTypeTargets: HTMLInputElement[]
  declare headerTargets: HTMLElement[]

  declare readonly hasStartButtonTarget: boolean
  declare readonly hasSyncButtonTarget: boolean
  declare readonly hasSpinnerTarget: boolean
  declare readonly hasErrorTarget: boolean
  declare readonly hasHeaderTarget: boolean

  private started = false
  private forceCreate = false
  private businessType = "individual"
  private statusCheckInterval: number | null = null

  get isTurboNative() {
    return document.documentElement.hasAttribute("data-turbo-native")
  }

  async start(event: any) {
    event?.preventDefault()
    if (this.started) return
    this.started = true

    // Retrieve forceCreate from event params (Stimulus 3)
    const forceCreateParam = event.params?.forceCreate
    this.forceCreate = forceCreateParam === true || forceCreateParam === "true"

    // Retrieve selected business type (if still present in UI) or use the value from attribute
    const selectedType = this.businessTypeTargets.find(input => input.checked)?.value
    this.businessType = selectedType || this.businessTypeValue || "individual"

    // Show loading state and hide config header
    this.headerTargets.forEach(header => {
      header.classList.add("hidden")
      header.style.display = "none"
    })

    if (this.hasStartButtonTarget) {
      this.startButtonTarget.disabled = true
    }

    if (this.hasSyncButtonTarget) {
      this.syncButtonTarget.disabled = true
    }
    
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("hidden")
    }

    // Default to Embedded flow even on Native unless explicitly requested
    // This provides a smoother UX (no leaving the app) and we've fixed popup handling.
    if (this.isTurboNative && this.useNativeValue) {
      await this.startNativeOnboarding()
    } else {
      await this.startEmbeddedOnboarding()
    }
  }

  private async startNativeOnboarding() {
    try {
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
      
      // Gather form data for prefilling (same as fetchClientSecret but for link)
      const payload: Record<string, string> = {}
      const fieldsToPrefill = document.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-stripe-prefill="true"]')
      fieldsToPrefill.forEach(input => {
        // Extract the field name, removing the `user[...]` part if present
        const name = input.name.replace(/user\[|\]/g, '')
        if (input.value) {
          payload[name] = input.value
        }
      })

      const response = await fetch(this.accountLinkUrlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        },
        body: JSON.stringify({ 
          ...payload, 
          force_create: this.forceCreate,
          business_type: this.businessType 
        }),
      })

      if (!response.ok) throw new Error("Failed to generate onboarding link")

      const { url } = await response.json()
      
      // Use the Bridge Browser component if available, else standard redirect
      // This follows the "stick to bridge pro components" advice.
      if (window.nativeBridge?.supportedComponents?.includes("browser")) {
        this.sendBridgeMessage("browser", "open", { url })
      } else {
        window.location.href = url
      }

      // Start polling for completion since the user is now in an external browser
      this.handleExit()
    } catch (e) {
      this.showError(e instanceof Error ? e.message : "Could not start native onboarding")
      this.resetLoadingState()
    }
  }

  private async startEmbeddedOnboarding() {
    try {
      await this.ensureConnectLoaded()

      const stripeConnectInstance = window.StripeConnect!.init({
        publishableKey: this.publishableKeyValue,
        fetchClientSecret: this.fetchClientSecret,
      })

      const component = stripeConnectInstance.create("account-onboarding")

      
      // @ts-ignore - Stripe Connect web components use custom 'exit' event
      component.addEventListener("exit", () => {
        this.handleExit()
      })

      this.containerTarget.replaceChildren(component)
      
      // Hide our local spinner once the Stripe component is added. 
      // Stripe will show its own loader while it's fetching the session.
      if (this.hasSpinnerTarget) {
        this.spinnerTarget.classList.add("hidden")
      }

      this.hideError()
    } catch (e) {
      this.showError(e instanceof Error ? e.message : "Could not initialize Stripe Connect")
      this.resetLoadingState()
    }
  }

  private resetLoadingState() {
    this.started = false
    this.forceCreate = false
    
    this.headerTargets.forEach(header => {
      header.classList.remove("hidden")
      header.style.display = ""
    })

    if (this.hasStartButtonTarget) {
      this.startButtonTarget.disabled = false
    }

    if (this.hasSyncButtonTarget) {
      this.syncButtonTarget.disabled = false
    }

    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add("hidden")
    }
  }

  private sendBridgeMessage(component: string, action: string, data: object = {}) {
    // Standard Bridge Component communication pattern
    const message = { component, action, data }
    window.nativeBridge?.postMessage(JSON.stringify(message))
  }

  private handleExit() {
    // Start checking for onboarding completion every 2 seconds
    let attempts = 0
    const maxAttempts = 15 // Check for up to 30 seconds

    if (this.statusCheckInterval !== null) {
      window.clearInterval(this.statusCheckInterval)
    }

    this.statusCheckInterval = window.setInterval(async () => {
      attempts++

      try {
        const response = await fetch(this.statusCheckUrlValue)
        if (!response.ok) {
          console.error("[StripeConnect] Status check failed", response.status)
          return
        }

        const json = (await response.json()) as { onboarding_complete?: boolean, status?: string }
        
        if (json.onboarding_complete) {
          if (this.statusCheckInterval !== null) {
            window.clearInterval(this.statusCheckInterval)
            this.statusCheckInterval = null
          }
          window.location.href = "/dashboard"
          return
        }
      } catch (e) {
        console.error("[StripeConnect] Error during status check:", e)
      }

      // Stop checking after max attempts
      if (attempts >= maxAttempts) {
        if (this.statusCheckInterval !== null) {
          window.clearInterval(this.statusCheckInterval)
          this.statusCheckInterval = null
        }
        
        // Instead of forcing redirect, let's just allow the user to try again or see instructions
        this.started = false
        if (this.hasStartButtonTarget) {
          this.startButtonTarget.disabled = false
          this.buttonTextTarget.textContent = "Retry Stripe Configuration"
        }
        if (this.hasSpinnerTarget) {
          this.spinnerTarget.classList.add("hidden")
        }
      }
    }, 2000)
  }

  private fetchClientSecret = async (): Promise<string | undefined> => {
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content

    const response = await fetch(this.accountSessionUrlValue, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      },
      body: JSON.stringify({ 
        force_create: this.forceCreate,
        business_type: this.businessType 
      }),
    })

    if (!response.ok) {
      let errorMessage = "Failed to start Stripe session"
      try {
        const errorData = await response.json()
        errorMessage = errorData.error || errorMessage
        console.error("[StripeConnect] API Error detail:", errorData)
      } catch (e) {
        console.error("[StripeConnect] API Error (no recovery):", response.status)
      }
      
      this.showError(errorMessage)
      this.resetLoadingState()
      return undefined
    }

    let json: { client_secret?: string, error?: string } = {}
    try {
      json = await response.json()
    } catch (e) {
      console.error("[StripeConnect] Failed to parse JSON response:", e)
      this.showError("Invalid response from server. Please try again.")
      this.resetLoadingState()
      return undefined
    }

    const clientSecret = json.client_secret

    if (!clientSecret) {
      console.error("[StripeConnect] No client secret in response")
      this.showError("Security token missing from response")
      this.resetLoadingState()
      return undefined
    }

    return clientSecret
  }

  private async ensureConnectLoaded(): Promise<void> {
    return new Promise((resolve, reject) => {
      // 1. Check if already loaded
      if (window.StripeConnect) {
        resolve()
        return
      }

      // 2. Inject script
      const scriptId = "stripe-connect-js"
      if (!document.getElementById(scriptId)) {
        const script = document.createElement("script")
        script.id = scriptId
        script.src = "https://connect-js.stripe.com/v1.0/connect.js"
        script.async = true
        script.onload = () => {
          // No resolve here - let the polling find the global object
        }
        script.onerror = (e) => {
          console.error("[StripeConnect] Script.onerror triggered:", e)
          reject(new Error("The Stripe Connect library could not be downloaded. Check your console's Network tab for 'connect.js' status on connect-js.stripe.com."))
        }
        document.head.appendChild(script)
      }


      // 3. Setup timeout
      const timeout = window.setTimeout(() => {
        window.clearInterval(checkInterval)
        const errorMsg = "StripeConnect failed to initialize within 15s. If you are sure there is no blocker, please verify you can open https://connect-js.stripe.com/v1.0/connect.js directly in your browser."
        console.error("[StripeConnect]", errorMsg)
        reject(new Error(errorMsg))
      }, 15000)

      // 4. Poll for the global object
      const checkInterval = window.setInterval(() => {
        if (window.StripeConnect) {
          window.clearTimeout(timeout)
          window.clearInterval(checkInterval)
          resolve()
        }
      }, 300)
    })
  }

  private showError(message?: string) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message || "An error occurred with Stripe Connect."
      this.errorTarget.classList.remove("hidden")
    }
  }

  private hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add("hidden")
    }
  }
}
